<template>
  <dashboard-layout title="Signals">
    <template #actions>
      <div class="tabs">
        <button :class="['tab-btn', { active: activeTab === 'signals' }]" @click="switchTab('signals')">Signals</button>
        <button :class="['tab-btn', { active: activeTab === 'unanswered' }]" @click="switchTab('unanswered')">Unanswered</button>
        <button :class="['tab-btn', { active: activeTab === 'archive' }]" @click="switchTab('archive')">Intent archive</button>
      </div>
      <filter-chips v-model="rangeDays" :options="rangeOptions" />
      <search-box v-model="searchQuery" placeholder="Search user / detail / model…" />
      <button class="refresh-btn" @click="reload" :disabled="cur.loading">Refresh</button>
    </template>

    <!-- Signal-type filter chips (signals tab only) -->
    <div v-if="activeTab === 'signals'" class="signal-chips">
      <button :class="['chip', { active: signalFilter === 'all' }]" @click="signalFilter = 'all'">All</button>
      <button
        v-for="s in SIGNAL_TYPES"
        :key="s"
        :class="['chip', { active: signalFilter === s, critical: CRITICAL.includes(s) }]"
        @click="signalFilter = s"
      >{{ signalLabel(s) }}</button>
    </div>

    <!-- Real gaps vs by-design split (unanswered tab). Real gaps (tool_gap +
         missing_data) is the default so the historical policy_boundary/other
         rows stop drowning the actionable backlog. -->
    <div v-if="activeTab === 'unanswered'" class="subtabs">
      <button :class="['subtab-btn', { active: unansweredView === 'gaps' }]" @click="switchUnansweredView('gaps')">
        Real gaps <span class="chip-count">{{ bucketCount('gaps') }}</span>
      </button>
      <button :class="['subtab-btn', { active: unansweredView === 'bydesign' }]" @click="switchUnansweredView('bydesign')">
        By design <span class="chip-count">{{ bucketCount('bydesign') }}</span>
      </button>
    </div>

    <!-- Gap facet chips scoped to the active bucket; tool_gap flagged. -->
    <div v-if="activeTab === 'unanswered'" class="signal-chips">
      <button :class="['chip', { active: gapFilter === 'all' }]" @click="gapFilter = 'all'">All <span class="chip-count">{{ activeBucketItems.length }}</span></button>
      <button
        v-for="g in gapChips"
        :key="g"
        :class="['chip', { active: gapFilter === g, critical: g === 'tool_gap' }]"
        @click="gapFilter = g"
      >{{ gapLabel(g) }} <span class="chip-count">{{ gapCount(g) }}</span></button>
    </div>

    <!-- Source filter (signals + unanswered) — tell judge-caught rows from model-logged ones -->
    <div v-if="activeTab !== 'archive'" class="source-filter">
      <span class="sf-label">Source</span>
      <button :class="['chip', { active: sourceFilter === 'all' }]" @click="sourceFilter = 'all'">All</button>
      <button :class="['chip', { active: sourceFilter === 'model' }]" @click="sourceFilter = 'model'">Model</button>
      <button :class="['chip', { active: sourceFilter === 'judge' }]" @click="sourceFilter = 'judge'">Judge</button>
    </div>

    <div v-if="error" class="error-banner">⚠ {{ error }}</div>

    <!-- Critical alert summary -->
    <div v-if="activeTab === 'signals' && criticalCount > 0" class="alert-banner">
      ⚠ {{ criticalCount }} critical signal{{ criticalCount === 1 ? '' : 's' }} in view
      ({{ countOf('crisis') }} crisis · {{ countOf('reality_detachment') }} reality detachment) — review for safety follow-up.
    </div>

    <div v-if="activeTab === 'archive'" class="note">
      The intent classifier was retired — <strong>ai-intent</strong> no longer receives new records. This is a read-only archive.
    </div>

    <div v-if="activeTab === 'unanswered'" class="note">
      Chat turns the assistant declined, deflected or only partly answered.
      <strong>Real gaps</strong> — tool gap (had the data, no tool: the feature backlog) and
      missing data (no birth time / connection not added) — are the default view.
      <strong>By design</strong> — policy boundary and other — are deliberate limits, kept
      apart so they don't drown the backlog.
      <strong>Judge</strong> rows were caught by the Haiku judge; from the next backend deploy
      the judge only logs real gaps, each tagged with a <strong>topic</strong>.
    </div>

    <!-- Topic breakdown for real gaps: topic → turns → distinct users. "No topic"
         is its own line (pre-deploy rows have no topic field). Click to filter. -->
    <div v-if="activeTab === 'unanswered' && unansweredView === 'gaps' && topicSummary.length" class="topic-summary">
      <div class="rs-head">Topics in range · {{ gapsForTopics.length }} real-gap turn{{ gapsForTopics.length === 1 ? '' : 's' }}</div>
      <table class="topic-table">
        <thead>
          <tr><th>Topic</th><th class="num">Turns</th><th class="num">Users</th></tr>
        </thead>
        <tbody>
          <tr
            v-for="t in topicSummary"
            :key="t.topic"
            :class="['topic-row', { active: topicFilter === t.topic, none: t.none }]"
            @click="toggleTopic(t.topic)"
          >
            <td>{{ t.label }}</td>
            <td class="num">{{ t.count }}</td>
            <td class="num">{{ t.users }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Free-text request volume for the by-design bucket (no topic tag there). -->
    <div v-if="activeTab === 'unanswered' && unansweredView === 'bydesign' && requestSummary.length" class="request-summary">
      <div class="rs-head">Requests by volume · {{ filteredItems.length }} shown</div>
      <div class="rs-list">
        <button
          v-for="r in requestSummary"
          :key="r.request"
          :class="['rs-item', { active: searchQuery.trim().toLowerCase() === r.request.toLowerCase() }]"
          :title="r.request"
          @click="toggleRequest(r.request)"
        >
          <span class="rs-count">{{ r.count }}</span>
          <span class="rs-req">{{ r.request }}</span>
        </button>
      </div>
    </div>

    <data-panel flush>
      <loading-state
        v-if="cur.loading && !cur.items.length"
        :label="`Loading ${activeTab === 'signals' ? 'signals' : (activeTab === 'unanswered' ? 'unanswered requests' : 'archive')}…`"
      />

      <template v-else>
        <div v-if="filteredItems.length" class="table-scroll-wrapper">
          <!-- Signals table -->
          <table v-if="activeTab === 'signals'" class="logs-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>User</th>
                <th>Signal</th>
                <th>Detail</th>
                <th>Provider</th>
                <th>Model</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in filteredItems" :key="item.id" class="log-row" :class="{ 'row-critical': CRITICAL.includes(item.signal) }">
                <td class="time-cell">{{ formatTime(item.timestamp) }}</td>
                <td>
                  <router-link v-if="item.userId" :to="`/user/${item.userId}`" class="user-link">{{ truncate(item.userId, 20) }}</router-link>
                  <span v-else class="muted">—</span>
                </td>
                <td><status-badge :variant="signalVariant(item.signal)" :label="signalLabel(item.signal)" /></td>
                <td class="detail-cell" :title="item.detail">{{ truncate(item.detail, 90) || '—' }}</td>
                <td>{{ item.provider || '—' }}</td>
                <td class="model-cell">{{ modelDisplay(item.model) }}</td>
              </tr>
            </tbody>
          </table>

          <!-- Unanswered-request table (grouped by gap, tool_gap first) -->
          <table v-else-if="activeTab === 'unanswered'" class="logs-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>User</th>
                <th>Gap</th>
                <th>Request</th>
                <th>Detail</th>
                <th>Answered</th>
                <th>Source</th>
                <th>Turn</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in filteredItems"
                :key="item.id"
                class="log-row row-clickable"
                :class="{ 'row-toolgap': item.gap === 'tool_gap' }"
                @click="openTurn(item, $event)"
              >
                <td class="time-cell">{{ formatTime(item.timestamp) }}</td>
                <td @click.stop>
                  <router-link v-if="item.userId" :to="`/user/${item.userId}`" class="user-link">{{ truncate(item.userId, 16) }}</router-link>
                  <span v-else class="muted">—</span>
                </td>
                <td><status-badge :variant="gapVariant(item.gap)" :label="gapLabel(item.gap)" /></td>
                <td class="req-cell" :title="item.request">{{ truncate(item.request, 40) || '—' }}</td>
                <td class="detail-cell" :title="item.detail">{{ truncate(item.detail, 80) || '—' }}</td>
                <td>
                  <status-badge v-if="item.answered" :variant="answeredVariant(item.answered)" :label="item.answered" />
                  <span v-else class="muted">—</span>
                </td>
                <td><status-badge :variant="sourceVariant(item.source)" :label="item.source || '—'" /></td>
                <td class="turn-cell" @click.stop>
                  <button v-if="item.userId" class="turn-link" @click="openTurn(item, $event)">
                    {{ (item.assistantMessageId || item.userMessageId) ? 'View turn ↗' : 'Open chat ↗' }}
                  </button>
                  <span v-else class="muted">—</span>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- Intent archive table -->
          <table v-else-if="activeTab === 'archive'" class="logs-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>User</th>
                <th>Message</th>
                <th>Intent</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in filteredItems" :key="item.id" class="log-row" @click="openIntent(item.id, $event)">
                <td class="time-cell">{{ formatTime(item.timestamp) }}</td>
                <td @click.stop>
                  <router-link v-if="item.userId" :to="`/user/${item.userId}`" class="user-link">{{ truncate(item.userId, 20) }}</router-link>
                  <span v-else class="muted">—</span>
                </td>
                <td class="detail-cell" :title="item.message">{{ truncate(item.message, 90) || '—' }}</td>
                <td><status-badge v-if="item.intent" variant="success" :label="item.intent" /><span v-else class="muted">—</span></td>
                <td><status-badge v-if="item.source" variant="primary" :label="item.source" /><span v-else class="muted">—</span></td>
              </tr>
            </tbody>
          </table>
        </div>
        <empty-state v-else :title="emptyMessage" />

        <div class="pagination-bar">
          <span class="page-info">
            {{ filteredItems.length.toLocaleString('en-US') }} shown
            <span v-if="hiddenByRange > 0" class="muted">· {{ hiddenByRange }} older than {{ rangeLabel }} hidden</span>
            <span class="muted">· {{ cur.items.length.toLocaleString('en-US') }} loaded</span>
          </span>
          <button v-if="cur.hasMore" class="load-btn" @click="loadMore" :disabled="cur.loading">
            {{ cur.loading ? 'Loading…' : 'Load more' }}
          </button>
          <span v-else class="muted end-note">End of results</span>
        </div>
      </template>
    </data-panel>
  </dashboard-layout>
</template>

<script>
import { ref, reactive, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { FirebaseSignalService } from '../services/firebaseSignalService'
import DashboardLayout from './ui/DashboardLayout.vue'
import DataPanel from './ui/DataPanel.vue'
import SearchBox from './ui/SearchBox.vue'
import FilterChips from './ui/FilterChips.vue'
import LoadingState from './ui/LoadingState.vue'
import EmptyState from './ui/EmptyState.vue'
import StatusBadge from './ui/StatusBadge.vue'

// The Unanswered tab reads the same ai-signals collection as Signals, then
// client-filters to signal == 'unanswered_request' (no composite index needed).
const COLLECTIONS = { signals: 'ai-signals', unanswered: 'ai-signals', archive: 'ai-intent' }
const SIGNAL_TYPES = ['crisis', 'reality_detachment', 'high_stakes', 'quality_complaint', 'quality_praise']
const CRITICAL = ['crisis', 'reality_detachment']
const UNANSWERED = 'unanswered_request'
const GAP_TYPES = ['tool_gap', 'missing_data', 'policy_boundary', 'other']
// Real, actionable gaps vs. deliberate limits. Everything that isn't tool_gap /
// missing_data (policy_boundary, other, or an unrecognised value) is treated as
// "by design" so no row is ever lost from the view.
const GAPS_BUCKET = ['tool_gap', 'missing_data']
const GAP_CHIPS = { gaps: ['tool_gap', 'missing_data'], bydesign: ['policy_boundary', 'other'] }
// Topic enum the judge writes for real gaps (backend astro-api 469d549 onward).
// Older rows have no `topic` field → bucketed under NO_TOPIC, shown separately.
const TOPIC_ORDER = [
  'add_connection_from_chat', 'forecast_horizon', 'synastry_between_connections',
  'intraday_transits', 'extra_points', 'past_date', 'app_help', 'report_access',
  'other_modality', 'other'
]
const TOPIC_LABELS = {
  add_connection_from_chat: 'Add connection from chat',
  forecast_horizon: 'Forecast horizon',
  synastry_between_connections: 'Synastry between connections',
  intraday_transits: 'Intraday transits',
  extra_points: 'Extra points',
  past_date: 'Past date',
  app_help: 'App help',
  report_access: 'Report access',
  other_modality: 'Other modality',
  other: 'Other'
}
const NO_TOPIC = '__none__'
const PAGE_SIZE = 50

function toMs(ts) {
  if (!ts) return 0
  if (typeof ts === 'object' && typeof ts.toMillis === 'function') return ts.toMillis()
  if (typeof ts === 'object' && (ts.seconds || ts._seconds)) return (ts.seconds || ts._seconds) * 1000
  const t = new Date(ts).getTime()
  return isNaN(t) ? 0 : t
}

export default {
  name: 'SignalsDashboard',
  components: { DashboardLayout, DataPanel, SearchBox, FilterChips, LoadingState, EmptyState, StatusBadge },
  setup() {
    const router = useRouter()
    const service = new FirebaseSignalService()

    const activeTab = ref('signals')
    const rangeDays = ref(7)
    const signalFilter = ref('all')
    const unansweredView = ref('gaps') // 'gaps' (real gaps, default) | 'bydesign'
    const gapFilter = ref('all')
    const topicFilter = ref('') // '' = all topics (gaps view only)
    const sourceFilter = ref('all')
    const searchQuery = ref('')
    const error = ref('')

    const ranges = [
      { days: 7, label: '7d' },
      { days: 30, label: '30d' },
      { days: 90, label: '90d' },
      { days: 0, label: 'All' }
    ]
    const rangeOptions = ranges.map(r => ({ value: r.days, label: r.label }))

    // Independent paged state per collection so switching tabs keeps each list.
    const state = reactive({
      signals: { items: [], cursor: null, hasMore: false, loading: false, loaded: false },
      unanswered: { items: [], cursor: null, hasMore: false, loading: false, loaded: false },
      archive: { items: [], cursor: null, hasMore: false, loading: false, loaded: false }
    })
    const cur = computed(() => state[activeTab.value])

    const loadPage = async (reset) => {
      const tab = activeTab.value
      const st = state[tab]
      st.loading = true
      error.value = ''
      try {
        if (reset) { st.cursor = null; st.items = [] }
        const page = await service.getPage(COLLECTIONS[tab], { pageSize: PAGE_SIZE, cursor: st.cursor })
        st.items = reset ? page.items : [...st.items, ...page.items]
        st.cursor = page.cursor
        st.hasMore = page.hasMore
        st.loaded = true
      } catch (e) {
        console.error(e)
        const code = e && e.code ? e.code : ''
        if (code === 'permission-denied') error.value = 'Firestore denied the read (not signed in). Reload and try again.'
        else if (code === 'failed-precondition') error.value = (e && e.message) || 'A Firestore index is required for this query.'
        else error.value = (e && e.message) || 'Failed to load.'
      } finally {
        st.loading = false
      }
    }

    const reload = () => loadPage(true)
    const loadMore = () => loadPage(false)

    const switchTab = (tab) => {
      if (activeTab.value === tab) return
      activeTab.value = tab
      signalFilter.value = 'all'
      unansweredView.value = 'gaps'
      gapFilter.value = 'all'
      topicFilter.value = ''
      sourceFilter.value = 'all'
      if (!state[tab].loaded) loadPage(true)
    }

    // Switching the real-gaps / by-design bucket resets the gap + topic facets,
    // which no longer apply to the other bucket.
    const switchUnansweredView = (view) => {
      if (unansweredView.value === view) return
      unansweredView.value = view
      gapFilter.value = 'all'
      topicFilter.value = ''
    }

    const cutoffMs = computed(() => rangeDays.value ? Date.now() - rangeDays.value * 86400000 : 0)

    const withinRange = (it) => {
      if (!cutoffMs.value) return true
      const ms = toMs(it.timestamp)
      return !ms || ms >= cutoffMs.value
    }

    const matchesSource = (it) => sourceFilter.value === 'all' || it.source === sourceFilter.value

    // Loaded unanswered_request rows passing range + source + search (NOT the gap
    // facet) — the base for gap-chip counts and the shown table.
    const unansweredInView = computed(() => {
      const q = searchQuery.value.toLowerCase().trim()
      return state.unanswered.items.filter(it => {
        if (it.signal !== UNANSWERED) return false
        if (!withinRange(it)) return false
        if (!matchesSource(it)) return false
        if (q) {
          const hay = `${it.userId || ''} ${it.request || ''} ${it.detail || ''} ${it.gap || ''} ${it.answered || ''} ${it.source || ''} ${it.model || ''}`.toLowerCase()
          if (!hay.includes(q)) return false
        }
        return true
      })
    })

    const gapRank = (g) => { const i = GAP_TYPES.indexOf(g); return i === -1 ? 99 : i }

    // Bucket split: real gaps (tool_gap / missing_data) vs everything else.
    const isRealGap = (g) => GAPS_BUCKET.includes(g)
    const gapsBucketItems = computed(() => unansweredInView.value.filter(it => isRealGap(it.gap)))
    const bydesignBucketItems = computed(() => unansweredInView.value.filter(it => !isRealGap(it.gap)))
    const activeBucketItems = computed(() => unansweredView.value === 'gaps' ? gapsBucketItems.value : bydesignBucketItems.value)
    const gapChips = computed(() => GAP_CHIPS[unansweredView.value] || [])
    const bucketCount = (view) => (view === 'gaps' ? gapsBucketItems.value : bydesignBucketItems.value).length

    // A row's topic key (missing/blank → NO_TOPIC). Only real-gap rows carry one.
    const topicKey = (it) => (typeof it.topic === 'string' && it.topic.trim()) ? it.topic.trim() : NO_TOPIC

    // Real-gap rows feeding the topic breakdown: respects the gap chip so the
    // summary matches the table, but not the topic chip (so you can switch topics).
    const gapsForTopics = computed(() =>
      gapsBucketItems.value.filter(it => gapFilter.value === 'all' || it.gap === gapFilter.value)
    )

    // topic → count + distinct users. Real topics by volume; "no topic" pinned last.
    const topicSummary = computed(() => {
      const map = new Map()
      gapsForTopics.value.forEach(it => {
        const k = topicKey(it)
        if (!map.has(k)) map.set(k, { count: 0, users: new Set() })
        const e = map.get(k)
        e.count++
        if (it.userId) e.users.add(it.userId)
      })
      const rows = [...map.entries()].map(([topic, e]) => ({
        topic,
        none: topic === NO_TOPIC,
        label: topicLabel(topic),
        count: e.count,
        users: e.users.size
      }))
      const rank = (t) => { const i = TOPIC_ORDER.indexOf(t); return i === -1 ? 99 : i }
      const real = rows.filter(r => !r.none).sort((a, b) => b.count - a.count || rank(a.topic) - rank(b.topic))
      const none = rows.filter(r => r.none)
      return [...real, ...none]
    })

    const toggleTopic = (t) => { topicFilter.value = topicFilter.value === t ? '' : t }

    const filteredItems = computed(() => {
      const tab = activeTab.value
      if (tab === 'unanswered') {
        let rows = activeBucketItems.value.filter(it => gapFilter.value === 'all' || it.gap === gapFilter.value)
        if (unansweredView.value === 'gaps' && topicFilter.value) {
          rows = rows.filter(it => topicKey(it) === topicFilter.value)
        }
        return rows
          .slice()
          .sort((a, b) => gapRank(a.gap) - gapRank(b.gap) || toMs(b.timestamp) - toMs(a.timestamp))
      }
      const q = searchQuery.value.toLowerCase().trim()
      const isSignals = tab === 'signals'
      return cur.value.items.filter(it => {
        if (!withinRange(it)) return false
        if (isSignals && signalFilter.value !== 'all' && it.signal !== signalFilter.value) return false
        if (isSignals && !matchesSource(it)) return false
        if (q) {
          const hay = isSignals
            ? `${it.userId || ''} ${it.signal || ''} ${it.detail || ''} ${it.model || ''} ${it.provider || ''} ${it.source || ''}`.toLowerCase()
            : `${it.userId || ''} ${it.message || ''} ${it.intent || ''} ${it.source || ''}`.toLowerCase()
          if (!hay.includes(q)) return false
        }
        return true
      })
    })

    const gapCount = (g) => unansweredInView.value.filter(it => it.gap === g).length

    // Count-by-request backlog summary, reflecting the shown rows (gap included).
    const requestSummary = computed(() => {
      const map = new Map()
      filteredItems.value.forEach(it => {
        const key = (it.request || '').trim() || '—'
        map.set(key, (map.get(key) || 0) + 1)
      })
      return [...map.entries()].map(([request, count]) => ({ request, count })).sort((a, b) => b.count - a.count)
    })

    const toggleRequest = (req) => {
      searchQuery.value = searchQuery.value.trim().toLowerCase() === req.toLowerCase() ? '' : req
    }

    // How many loaded rows are hidden purely because they fall outside the range.
    const hiddenByRange = computed(() => {
      if (!cutoffMs.value) return 0
      if (activeTab.value === 'unanswered') {
        return state.unanswered.items.filter(it => it.signal === UNANSWERED && !withinRange(it)).length
      }
      return cur.value.items.filter(it => !withinRange(it)).length
    })

    const countOf = (sig) => filteredItems.value.filter(it => it.signal === sig).length
    const criticalCount = computed(() => filteredItems.value.filter(it => CRITICAL.includes(it.signal)).length)

    const rangeLabel = computed(() => {
      const r = ranges.find(x => x.days === rangeDays.value)
      return r ? r.label : `${rangeDays.value}d`
    })

    const emptyMessage = computed(() => {
      if (activeTab.value === 'signals') return 'No signals in the selected range'
      if (activeTab.value === 'unanswered') return 'No unanswered requests in the selected range'
      return 'No archived intent logs in the selected range'
    })

    const signalLabel = (s) => s ? s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '—'
    const signalVariant = (s) => {
      if (s === 'crisis' || s === 'reality_detachment') return 'danger'
      if (s === 'high_stakes') return 'warning'
      if (s === 'quality_praise') return 'success'
      if (s === 'quality_complaint') return 'primary'
      if (s === UNANSWERED) return 'info'
      return 'neutral'
    }
    const GAP_LABELS = { tool_gap: 'Tool gap', missing_data: 'Missing data', policy_boundary: 'Policy boundary', other: 'Other' }
    const gapLabel = (g) => GAP_LABELS[g] || (g ? signalLabel(g) : '—')
    const topicLabel = (t) => t === NO_TOPIC ? 'No topic (pre-deploy)' : (TOPIC_LABELS[t] || signalLabel(t))
    const gapVariant = (g) => {
      if (g === 'tool_gap') return 'warning'
      if (g === 'missing_data') return 'info'
      return 'neutral'
    }
    const answeredVariant = (a) => a === 'declined' ? 'danger' : (a === 'partial' ? 'warning' : 'neutral')
    const sourceVariant = (s) => s === 'judge' ? 'info' : 'neutral'
    const modelDisplay = (m) => m ? m.replace(/_/g, '.') : '—'
    const truncate = (t, max) => !t ? '' : (t.length > max ? t.substring(0, max) + '…' : t)

    const formatTime = (ts) => {
      const ms = toMs(ts)
      if (!ms) return '—'
      return new Date(ms).toLocaleString('tr-TR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
    }

    const openIntent = (id, event) => {
      if (event.metaKey || event.ctrlKey) window.open(`/intent-log/${id}`, '_blank')
      else router.push(`/intent-log/${id}`)
    }

    // Open the user's chat at the exact turn. Judge rows carry message ids; model
    // rows don't, so fall back to just opening the chat tab.
    const openTurn = (item, event) => {
      if (!item.userId) return
      const msg = item.assistantMessageId || item.userMessageId || ''
      const to = msg ? `/user/${item.userId}?tab=chat&msg=${encodeURIComponent(msg)}` : `/user/${item.userId}?tab=chat`
      if (event && (event.metaKey || event.ctrlKey)) window.open(to, '_blank')
      else router.push(to)
    }

    // Reset the signal filter when it no longer applies; range/search are pure view filters.
    watch(activeTab, () => { searchQuery.value = '' })

    onMounted(() => loadPage(true))

    return {
      activeTab, rangeDays, ranges, rangeOptions, signalFilter,
      unansweredView, gapFilter, topicFilter, sourceFilter, searchQuery, error,
      SIGNAL_TYPES, CRITICAL,
      cur, filteredItems, unansweredInView, requestSummary, hiddenByRange, criticalCount, rangeLabel, emptyMessage,
      gapChips, activeBucketItems, gapsForTopics, topicSummary, bucketCount,
      reload, loadMore, switchTab, switchUnansweredView, countOf, gapCount, toggleRequest, toggleTopic,
      signalLabel, signalVariant, gapLabel, gapVariant, answeredVariant, sourceVariant,
      modelDisplay, truncate, formatTime, openIntent, openTurn
    }
  }
}
</script>

<style scoped>
/* Tabs (segmented control — page-specific) */
.tabs {
  display: flex;
  gap: 0;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
}
.tab-btn {
  border: none;
  border-radius: 0;
  padding: 8px 14px;
  background: transparent;
  color: var(--text-primary);
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}
.tab-btn:not(:last-child) { border-right: 1px solid rgba(255, 255, 255, 0.1); }
.tab-btn:hover { background: rgba(255, 255, 255, 0.08); }
.tab-btn.active {
  background: linear-gradient(135deg, var(--primary-color), var(--gradient-2));
  box-shadow: 0 2px 8px rgba(108, 99, 255, 0.3);
}

.refresh-btn {
  padding: 8px 14px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 13px;
  cursor: pointer;
}
.refresh-btn:hover { background: rgba(255, 255, 255, 0.1); }
.refresh-btn:disabled { opacity: 0.5; cursor: default; }

/* Signal-type chips (critical highlighting — page-specific) */
.signal-chips { display: flex; gap: 6px; flex-wrap: wrap; }
.chip {
  padding: 6px 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  color: var(--text-secondary);
  font-size: 12.5px;
  cursor: pointer;
  transition: all 0.2s ease;
}
.chip:hover { color: var(--text-primary); }
.chip.active { background: var(--primary-color); border-color: var(--primary-color); color: #fff; }
.chip.critical { border-color: rgba(255, 71, 87, 0.5); color: #ff8a95; }
.chip.critical.active { background: #ff4757; border-color: #ff4757; color: #fff; }
.chip-count { opacity: 0.65; font-size: 11px; margin-left: 3px; }

/* Source filter (model / judge / all) */
.source-filter { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.sf-label { font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; color: var(--text-secondary); margin-right: 2px; }

/* Backlog summary — count by request (unanswered tab) */
.request-summary {
  background: var(--panel-bg, rgba(255, 255, 255, 0.03));
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 12px 14px;
}
.rs-head { font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; color: var(--text-secondary); margin-bottom: 10px; }
.rs-list { display: flex; flex-wrap: wrap; gap: 8px; }
.rs-item {
  display: inline-flex; align-items: center; gap: 8px; max-width: 340px;
  padding: 5px 12px 5px 5px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  color: var(--text-primary); font-size: 12.5px; cursor: pointer;
  transition: all 0.2s ease;
}
.rs-item:hover { background: rgba(255, 255, 255, 0.1); }
.rs-item.active { border-color: var(--primary-color); background: rgba(108, 99, 255, 0.18); }
.rs-count {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 22px; height: 22px; padding: 0 6px;
  background: var(--primary-color); color: #fff;
  border-radius: 11px; font-size: 12px; font-weight: 600;
}
.rs-req { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

/* Real gaps / by-design sub-toggle (unanswered tab) */
.subtabs { display: inline-flex; gap: 0; background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255, 255, 255, 0.1); border-radius: 8px; overflow: hidden; }
.subtab-btn {
  border: none; border-radius: 0; padding: 7px 16px;
  background: transparent; color: var(--text-primary);
  font-size: 13px; cursor: pointer; transition: all 0.2s ease; white-space: nowrap;
}
.subtab-btn:first-child { border-right: 1px solid rgba(255, 255, 255, 0.1); }
.subtab-btn:hover { background: rgba(255, 255, 255, 0.08); }
.subtab-btn.active { background: linear-gradient(135deg, var(--primary-color), var(--gradient-2)); color: #fff; box-shadow: 0 2px 8px rgba(108, 99, 255, 0.3); }

/* Topic breakdown table (real-gaps view) */
.topic-summary {
  background: var(--panel-bg, rgba(255, 255, 255, 0.03));
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 12px 14px;
}
.topic-table { width: 100%; border-collapse: collapse; margin-top: 6px; }
.topic-table th, .topic-table td { text-align: left; padding: 7px 10px; font-size: 13px; }
.topic-table th { font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; color: var(--text-secondary); font-weight: 600; }
.topic-table th.num, .topic-table td.num { text-align: right; width: 80px; }
.topic-table tbody tr { border-top: 1px solid rgba(255, 255, 255, 0.07); cursor: pointer; transition: background-color 0.2s ease; }
.topic-table tbody tr:hover { background: rgba(255, 255, 255, 0.05); }
.topic-row.active { background: rgba(108, 99, 255, 0.18); }
.topic-row.active td:first-child { box-shadow: inset 3px 0 0 var(--primary-color); }
.topic-row.none td { color: var(--text-secondary); font-style: italic; }
.topic-table td.num { font-variant-numeric: tabular-nums; color: var(--text-primary); }

/* Banners (page-specific) */
.error-banner {
  background: rgba(248, 191, 113, 0.12);
  border: 1px solid rgba(248, 191, 113, 0.35);
  color: #f8bf71;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
}
.alert-banner {
  background: rgba(255, 71, 87, 0.12);
  border: 1px solid rgba(255, 71, 87, 0.4);
  color: #ff8a95;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
  font-weight: 500;
}
.note { margin: 0; font-size: 12.5px; color: var(--text-secondary); }

/* Table (custom — needs per-row critical highlighting) */
.table-scroll-wrapper { width: 100%; overflow-x: auto; }
.logs-table { width: 100%; border-collapse: separate; border-spacing: 0; }
.logs-table th, .logs-table td {
  padding: 10px 12px;
  text-align: left;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}
.logs-table thead th {
  position: sticky; top: 0; z-index: 1;
  font-weight: 600; color: var(--text-secondary); font-size: 11px;
  text-transform: uppercase; letter-spacing: 0.5px;
  background: rgba(26, 27, 46, 0.98);
}
.logs-table td { color: var(--text-primary); font-size: 13px; }
.time-cell { white-space: nowrap; }
.detail-cell { max-width: 360px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.req-cell { max-width: 220px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-weight: 500; }
.model-cell { font-size: 12px; color: var(--text-secondary); }
.turn-cell { white-space: nowrap; }
.turn-link {
  padding: 4px 10px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 6px;
  color: var(--primary-color);
  font-size: 12px; cursor: pointer; white-space: nowrap;
}
.turn-link:hover { background: rgba(255, 255, 255, 0.1); }
.user-link { color: var(--primary-color); text-decoration: none; }
.user-link:hover { text-decoration: underline; }
.muted { color: var(--text-secondary); }

.log-row { transition: background-color 0.2s ease; }
.log-row:hover { background: rgba(255, 255, 255, 0.05); }
.row-clickable { cursor: pointer; }
.row-critical { background: rgba(255, 71, 87, 0.08); }
.row-critical:hover { background: rgba(255, 71, 87, 0.14); }
.row-critical td:first-child { box-shadow: inset 3px 0 0 #ff4757; }
.row-toolgap td:first-child { box-shadow: inset 3px 0 0 #f8bf71; }

/* Pagination (page-specific) */
.pagination-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}
.page-info { font-size: 12.5px; color: var(--text-primary); }
.end-note { font-size: 12.5px; }
.load-btn {
  padding: 8px 18px;
  background: var(--primary-color);
  border: none;
  border-radius: 8px;
  color: #fff;
  font-size: 13px;
  cursor: pointer;
}
.load-btn:hover { opacity: 0.92; }
.load-btn:disabled { opacity: 0.5; cursor: default; }
</style>
